import React, { useState, useRef, useEffect } from "react"
import { useAuth } from "../hooks/useAuth"
import { useIsMobile } from "../hooks/useIsMobile"
import { useParams, useNavigate } from "react-router-dom"
import { api } from "../services/api"
import { useQuote, useQuotes } from "../hooks/useQuotes"
import { useImageManager } from "../hooks/useImageManager"
import Header from "../components/Header"
import { FlightCard, HotelCard, CarCard, InsuranceCard, BusCard, PassportCard, VisaCard } from "../components/blocks"
import { ImagesModal } from "../components/quotation"
import { colors, borderRadius } from "../styles/tokens"
import { MapPin, Bus, Compass, Upload, RefreshCw } from "lucide-react"

const CRM_API_URL = process.env.REACT_APP_CRM_API_URL || 'http://localhost:5000/api/crm'
const PIPELINE_URL = CRM_API_URL.replace('/api/crm', '/api/pipeline')

const blockTypes = [
  { value: "flight", label: "Voo", icon: "✈️" },
  { value: "hotel", label: "Hotel", icon: "🏨" },
  { value: "bus", label: "Rodoviário", icon: "🚌" },
  { value: "transfer", label: "Transfer", icon: "🚐" },
  { value: "car", label: "Carro", icon: "🚗" },
  { value: "tour", label: "Passeio", icon: "🌍" },
  { value: "insurance", label: "Seguro", icon: "🛡️" }
]

export default function QuoteEditor() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { quote, isLoading, refresh } = useQuote(id)
  const { refresh: refreshQuotes } = useQuotes()

  const { profile } = useAuth()
  const [showBlockForm, setShowBlockForm] = useState(false)
  const [blockForm, setBlockForm] = useState({
    type: "flight",
    date: "",
    title: "",
    description: "",
    image_url: "",
    price: ""
  })

  // Reprocessamento
  const [reprocessing, setReprocessing] = useState(false)
  const [reprocessResult, setReprocessResult] = useState(null)
  const fileInputRef = useRef(null)

  // Markup
  const [editingMarkup, setEditingMarkup] = useState(false)
  const [markupValue, setMarkupValue] = useState("")

  // Pipeline
  const [sending, setSending] = useState(false)
  const [contactInfo, setContactInfo] = useState(null)

  // AI Correction (pós-publicação)
  const [aiInstruction, setAiInstruction] = useState('')
  const [aiCorrecting, setAiCorrecting] = useState(false)
  const [aiResult, setAiResult] = useState(null)

  // Derive (nova opção)
  const [showDeriveModal, setShowDeriveModal] = useState(false)
  const [optionName, setOptionName] = useState('')
  const [deriving, setDeriving] = useState(false)

  // Image verification on publish
  const {
    hotelImages,
    carImages,
    tourImages,
    handleImageChange,
    loadImagesForBlocks,
    saveAllImages,
    validateAllImages
  } = useImageManager()
  const [showImagesModal, setShowImagesModal] = useState(false)
  const [imageUrl1, setImageUrl1] = useState('')
  const [imageUrl2, setImageUrl2] = useState('')
  const [existingImages, setExistingImages] = useState({ img1: null, img2: null })
  const [publishing, setPublishing] = useState(false)

  // Busca dados do contato no CRM quando contact_id estiver disponível
  useEffect(() => {
    const contactId = quote?.contact_id
    if (!contactId) return
    fetch(`${CRM_API_URL}/contacts/${contactId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setContactInfo(data) })
      .catch(() => {})
  }, [quote?.contact_id])

  const handleAddBlock = async (e) => {
    e.preventDefault()
    await api.addBlock(id, { ...blockForm, price: parseFloat(blockForm.price) || 0 })
    setShowBlockForm(false)
    setBlockForm({ type: "flight", date: "", title: "", description: "", image_url: "", price: "" })
    refresh()
  }

  const handleRemoveBlock = async (blockId) => {
    if (window.confirm("Remover este item?")) {
      await api.removeBlock(id, blockId)
      refresh()
    }
  }

  const handlePublish = async () => {
    if (!quote) return
    setPublishing(true)

    try {
      // 1. Verifica imagens do destino
      const destImages = await api.getDestinationImages(quote.destination || '')
      const hasImg1 = !!(destImages?.destination_image_url?.trim())
      const hasImg2 = !!(destImages?.destination_image_url_2?.trim())

      setExistingImages({
        img1: hasImg1 ? destImages.destination_image_url : null,
        img2: hasImg2 ? destImages.destination_image_url_2 : null
      })
      if (hasImg1) setImageUrl1(destImages.destination_image_url)
      if (hasImg2) setImageUrl2(destImages.destination_image_url_2)

      // 2. Verifica imagens dos blocos
      const loadedImages = await loadImagesForBlocks(quote.blocks || [])

      const needsDestination = !hasImg1 || !hasImg2
      const allBlocksOk = validateAllImages(
        loadedImages.hotels,
        loadedImages.cars,
        loadedImages.tours
      )

      // 3. Se falta algo, abre modal
      if (needsDestination || !allBlocksOk) {
        setShowImagesModal(true)
        setPublishing(false)
        return
      }

      // 4. Aplica imagens nos blocos e publica
      await applyImagesAndPublish(
        destImages.destination_image_url,
        destImages.destination_image_url_2,
        loadedImages.hotels,
        loadedImages.cars,
        loadedImages.tours
      )
    } catch (err) {
      console.error('Erro ao verificar imagens:', err)
      // Fallback: publica sem imagens
      await api.publishQuote(id)
      refresh()
      refreshQuotes()
    } finally {
      setPublishing(false)
    }
  }

  // Aplica imagens do cache nos blocos e publica
  const applyImagesAndPublish = async (img1, img2, hotelsImgs, carsImgs, toursImgs) => {
    try {
      // Atualiza image_url dos blocos que têm imagem no cache
      for (const block of (quote.blocks || [])) {
        let imgs = null
        if (block.type === 'hotel') imgs = hotelsImgs?.[block.title]
        else if (block.type === 'car') imgs = carsImgs?.[block.title]
        else if (block.type === 'tour') imgs = toursImgs?.[block.title]

        if (imgs && imgs.img1 && imgs.img1 !== block.image_url) {
          await api.editBlock(id, block.id, {
            image_url: imgs.img1,
            details: {
              ...(block.details || {}),
              image_url_2: imgs.img2 || undefined,
              image_url_3: imgs.img3 || undefined
            }
          })
        }
      }

      await api.publishQuote(id)
      refresh()
      refreshQuotes()
    } catch (err) {
      console.error('Erro ao aplicar imagens:', err)
      await api.publishQuote(id)
      refresh()
      refreshQuotes()
    }
  }

  // Handler do ImagesModal — salva e publica
  const handleImagesSubmit = async () => {
    setPublishing(true)
    try {
      await saveAllImages()
      await api.saveDestinationImages(quote.destination, imageUrl1, imageUrl2)

      // Aplica nos blocos
      for (const block of (quote.blocks || [])) {
        let imgs = null
        if (block.type === 'hotel') imgs = hotelImages?.[block.title]
        else if (block.type === 'car') imgs = carImages?.[block.title]
        else if (block.type === 'tour') imgs = tourImages?.[block.title]

        if (imgs && imgs.img1) {
          await api.editBlock(id, block.id, {
            image_url: imgs.img1,
            details: {
              ...(block.details || {}),
              image_url_2: imgs.img2 || undefined,
              image_url_3: imgs.img3 || undefined
            }
          })
        }
      }

      await api.publishQuote(id)
      setShowImagesModal(false)
      refresh()
      refreshQuotes()
    } catch (err) {
      console.error('Erro ao salvar imagens:', err)
      alert('Erro ao salvar imagens: ' + (err?.message || ''))
    } finally {
      setPublishing(false)
    }
  }

  const validateImages = () => {
    if (!String(imageUrl1 || '').trim() || !String(imageUrl2 || '').trim()) return false
    return validateAllImages()
  }

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/c/${quote.public_link}`)
    alert("Link copiado!")
  }

  // Reprocessamento
  const handleReprocessClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    setReprocessing(true)
    setReprocessResult(null)

    try {
      const result = await api.reprocessQuote(id, files)
      setReprocessResult(result)

      // "Adicionar documentos" sempre complementa ESTA cotação.
      // Nunca cria cotação filha — não há para onde navegar.
      if (result.action === "enriched") {
        refresh()
        refreshQuotes()
      }
    } catch (error) {
      alert("Erro ao reprocessar: " + error.message)
    } finally {
      setReprocessing(false)
      e.target.value = "" // Reset input
    }
  }

  // Markup
  const handleMarkupSave = async () => {
    const value = parseFloat(markupValue) || 0
    await api.updateMarkup(id, value)
    setEditingMarkup(false)
    refresh()
  }

  const startEditingMarkup = () => {
    setMarkupValue("")
    setEditingMarkup(true)
  }

  // Pipeline handlers
  const handleSendWhatsApp = async () => {
    setSending(true)
    try {
      const resp = await fetch(`${PIPELINE_URL}/quotes/${id}/send-whatsapp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }
      })
      const data = await resp.json()
      if (resp.ok) {
        alert(`Cotação enviada para ${data.phone}!`)
        refresh() // atualiza quote.pipeline_status da nascente única
      } else {
        alert(data.error || 'Erro ao enviar')
      }
    } catch (e) {
      alert('Erro ao enviar: ' + e.message)
    } finally {
      setSending(false)
    }
  }

  // AI Correction handler
  const handleAiCorrect = async () => {
    if (!aiInstruction.trim()) return
    setAiCorrecting(true)
    setAiResult(null)
    try {
      const result = await api.aiCorrect(id, aiInstruction)
      if (result.error) {
        setAiResult({ type: 'error', message: result.error })
      } else {
        setAiResult({ type: 'success', message: result.summary || 'Correção aplicada!' })
        setAiInstruction('')
        refresh()
      }
    } catch (e) {
      setAiResult({ type: 'error', message: e.message || 'Erro ao corrigir' })
    } finally {
      setAiCorrecting(false)
    }
  }

  // Derive handler (nova opção)
  const handleDerive = async () => {
    setDeriving(true)
    try {
      const result = await api.deriveQuote(id, optionName)
      if (result.error) {
        alert('Erro ao criar opção: ' + result.error)
      } else {
        setShowDeriveModal(false)
        setOptionName('')
        navigate(`/app/cotacao/${result.id}`)
      }
    } catch (e) {
      alert('Erro: ' + e.message)
    } finally {
      setDeriving(false)
    }
  }

  const formatPrice = (price) => {
    return (price || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ""
    const date = new Date(dateStr + "T00:00:00")
    return date.toLocaleDateString("pt-BR")
  }

  const buttonOutline = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.75rem 1.25rem",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: colors.primary,
    backgroundColor: "transparent",
    border: `1px solid ${colors.primary}`,
    borderRadius: borderRadius.sm,
    cursor: "pointer"
  }

  const buttonPrimary = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.75rem 1.5rem",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: colors.white,
    backgroundColor: colors.accent,
    border: "none",
    borderRadius: borderRadius.sm,
    cursor: "pointer"
  }

  const buttonSecondary = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "0.75rem 1.25rem",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: colors.white,
    backgroundColor: colors.primary,
    border: "none",
    borderRadius: borderRadius.sm,
    cursor: "pointer"
  }

  // Atualização otimista: atualiza bloco no estado local sem refetch
  const handleBlockUpdate = (blockId, updatedBlock) => {
    // O hook useQuote expõe refresh(); aqui fazemos refresh pra recalcular totais
    refresh()
  }

  const renderBlock = (block) => {
    const editProps = { quoteId: id, onBlockUpdate: handleBlockUpdate }
    switch (block.type) {
      case "flight":
        return <FlightCard key={block.id} block={block} onRemove={handleRemoveBlock} {...editProps} />
      case "hotel":
        return <HotelCard key={block.id} block={block} onRemove={handleRemoveBlock} {...editProps} />
      case "car":
        return <CarCard key={block.id} block={block} onRemove={handleRemoveBlock} {...editProps} />
      case "insurance":
        return <InsuranceCard key={block.id} block={block} onRemove={handleRemoveBlock} {...editProps} />
      case "bus":
        return <BusCard key={block.id} block={block} onRemove={handleRemoveBlock} {...editProps} />
      case "passport":
        return <PassportCard key={block.id} block={block} onRemove={handleRemoveBlock} {...editProps} />
      case "visa":
        return <VisaCard key={block.id} block={block} onRemove={handleRemoveBlock} {...editProps} />
      default:
        return (
          <div
            key={block.id}
            style={{
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
              borderRadius: borderRadius.md,
              padding: "1.5rem",
              position: "relative"
            }}
          >
            <button
              onClick={() => handleRemoveBlock(block.id)}
              style={{
                position: "absolute",
                top: "1rem",
                right: "1rem",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: colors.text.muted,
                fontSize: "1.25rem"
              }}
            >
              ×
            </button>

            <div style={{ display: "flex", gap: "1rem" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  backgroundColor: `${colors.primary}10`,
                  borderRadius: borderRadius.sm,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                {block.type === "transfer" && <Bus size={24} color={colors.primary} />}
                {block.type === "tour" && <Compass size={24} color={colors.primary} />}
                {!["transfer", "tour"].includes(block.type) && <MapPin size={24} color={colors.primary} />}
              </div>

              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: "1.125rem", fontWeight: 600, color: colors.primary, margin: "0 0 1rem 0" }}>
                  {block.title}
                </h4>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                  {block.description && (
                    <div>
                      <p style={{ fontSize: "0.75rem", color: colors.text.muted, marginBottom: "0.25rem" }}>
                        Descrição
                      </p>
                      <p style={{ fontSize: "0.875rem", fontWeight: 500, color: colors.text.primary, margin: 0 }}>
                        {block.description}
                      </p>
                    </div>
                  )}

                  <div>
                    <p style={{ fontSize: "0.75rem", color: colors.text.muted, marginBottom: "0.25rem" }}>Data</p>
                    <p style={{ fontSize: "0.875rem", fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                      {formatDate(block.date)}
                    </p>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <p style={{ fontSize: "0.75rem", color: colors.text.muted, marginBottom: "0.25rem" }}>Valor</p>
                    <p style={{ fontSize: "1.25rem", fontWeight: 700, color: colors.accent, margin: 0 }}>
                      R$ {formatPrice(block.price)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
    }
  }

  // Mostra loading apenas na primeira carga (sem dados em cache)
  if (isLoading && !quote) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: colors.background }}>
        <Header subtitle="Detalhes da cotação" userName={profile?.name || "Usuário"} />
        <main style={{ maxWidth: "1024px", margin: "0 auto", padding: "2rem 1.5rem" }}>
          <p style={{ color: colors.text.secondary }}>Carregando...</p>
        </main>
      </div>
    )
  }

  if (!quote) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: colors.background }}>
        <Header subtitle="Detalhes da cotação" userName={profile?.name || "Usuário"} />
        <main style={{ maxWidth: "1024px", margin: "0 auto", padding: "2rem 1.5rem" }}>
          <p style={{ color: colors.text.secondary }}>Cotação não encontrada.</p>
        </main>
      </div>
    )
  }

  // Calcula preços
  const totalPrice = quote.total_price || 0
  const markup = quote.markup || 0
  const finalPrice = totalPrice + markup

  return (
    <div style={{ minHeight: "100vh", backgroundColor: colors.background, fontFamily: "'Inter', sans-serif" }}>
      <Header subtitle="Detalhes da cotação" userName={profile?.name || "Usuário"} />

      {/* Input oculto para upload */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.png,.jpg,.jpeg"
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      <main style={{ maxWidth: "1024px", margin: "0 auto", padding: isMobile ? "1rem" : "2rem 1.5rem", paddingBottom: isMobile ? "80px" : "2rem" }}>
        {/* Cabeçalho */}
        <div
          style={{
            backgroundColor: colors.card,
            borderRadius: borderRadius.lg,
            border: `1px solid ${colors.border}`,
            padding: isMobile ? "1rem" : "1.5rem",
            marginBottom: "1.5rem",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "stretch" : "center",
            gap: isMobile ? "1rem" : 0
          }}
        >
          <div>
            <h2 style={{ fontSize: "1.5rem", fontWeight: 600, color: colors.primary, margin: "0 0 0.25rem 0" }}>
              {quote.client_name}
            </h2>
            <p style={{ fontSize: "1rem", color: colors.text.secondary, margin: 0 }}>{quote.destination}</p>
          </div>

          <div style={{ display: "flex", gap: "0.75rem", overflowX: isMobile ? "auto" : "visible", WebkitOverflowScrolling: "touch" }}>
            {!isMobile && (
              <button
                onClick={handleReprocessClick}
                disabled={reprocessing}
                style={{
                  ...buttonSecondary,
                  opacity: reprocessing ? 0.7 : 1
                }}
              >
                {reprocessing ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  <Upload size={16} />
                )}
                {reprocessing ? "Processando..." : "Adicionar documentos"}
              </button>
            )}

            {isMobile ? (
              <button onClick={() => navigate("/app")} style={{ ...buttonOutline, flexShrink: 0, whiteSpace: "nowrap" }}>
                ←
              </button>
            ) : (
              <button onClick={() => navigate("/app")} style={buttonOutline}>
                ← Voltar
              </button>
            )}
          </div>
        </div>

        {/* Resultado do reprocessamento */}
        {reprocessResult && reprocessResult.action === "enriched" && (
          <div
            style={{
              backgroundColor: "#d4edda",
              border: "1px solid #c3e6cb",
              borderRadius: borderRadius.md,
              padding: "1rem 1.5rem",
              marginBottom: "1.5rem"
            }}
          >
            <p style={{ margin: 0, color: "#155724", fontWeight: 500 }}>
              ✓ Cotação atualizada com sucesso!
            </p>
            <ul style={{ margin: "0.5rem 0 0 0", paddingLeft: "1.25rem", color: "#155724" }}>
              {reprocessResult.changes.map((change, i) => (
                <li key={i}>{change}</li>
              ))}
            </ul>
          </div>
        )}

        {reprocessResult && reprocessResult.action === "no_change" && (
          <div
            style={{
              backgroundColor: "#fff3cd",
              border: "1px solid #ffeeba",
              borderRadius: borderRadius.md,
              padding: "1rem 1.5rem",
              marginBottom: "1.5rem"
            }}
          >
            <p style={{ margin: 0, color: "#856404", fontWeight: 500 }}>
              Nenhuma informação nova encontrada nos documentos.
            </p>
          </div>
        )}

        {/* Conteúdo principal */}
        <div
          style={{
            backgroundColor: colors.card,
            borderRadius: borderRadius.lg,
            border: `1px solid ${colors.border}`,
            padding: "2rem"
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1.5rem"
            }}
          >
            <h3 style={{ fontSize: "1.25rem", fontWeight: 700, color: colors.primary, margin: 0 }}>
              Itens da Cotação
              {quote.chosen_option_id === quote.id && (
                <span style={{ fontSize: "11px", background: "#dbeafe", color: "#1e40af", padding: "2px 8px", borderRadius: "4px", fontWeight: 700, marginLeft: "10px", verticalAlign: "middle" }}>
                  ✓ Cliente escolheu esta opção
                </span>
              )}
            </h3>
            <button onClick={() => setShowBlockForm(true)} style={buttonPrimary}>
              + Adicionar Item
            </button>
          </div>

          {quote.blocks.length === 0 ? (
            <p style={{ color: colors.text.secondary }}>Nenhum item adicionado.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {quote.blocks.map((block) => renderBlock(block))}
            </div>
          )}

          {/* Opções alternativas (filhas) */}
          {(quote.children || []).length > 0 && (
            <div style={{ marginTop: "1.5rem", paddingTop: "1.5rem", borderTop: `1px solid ${colors.border}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                <h3 style={{ fontSize: "1rem", fontWeight: 700, color: colors.primary, margin: 0 }}>
                  Opções alternativas ({quote.children.length})
                </h3>
                <span style={{ fontSize: "12px", color: colors.text.muted }}>
                  O cliente vê todas as opções lado a lado
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                {quote.children.map((child, idx) => {
                  const childBlocks = child.blocks || []
                  const firstFlight = childBlocks.find(b => b.type === "flight")
                  const det = firstFlight?.details || {}
                  const segs = det.segments || []
                  const depTime = det.departure_time || segs[0]?.departure_time || "--:--"
                  const arrTime = det.arrival_time || segs[segs.length - 1]?.arrival_time || "--:--"
                  const airline = segs[0]?.airline || det.airline || ""
                  const diff = (child.final_price || child.total_price || 0) - (quote.final_price || quote.total_price || 0)
                  const diffStr = diff === 0 ? "mesmo preço" : diff > 0 ? `+R$ ${Math.abs(diff).toFixed(2).replace(".", ",")}` : `-R$ ${Math.abs(diff).toFixed(2).replace(".", ",")}`
                  const diffColor = diff > 0 ? "#d97706" : diff < 0 ? "#059669" : colors.text.muted

                  return (
                    <div key={child.id} style={{
                      border: `1px solid ${colors.border}`,
                      borderRadius: "8px",
                      padding: "12px 16px",
                      background: "#fafafa",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px"
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                          <span style={{ fontSize: "11px", fontWeight: 700, color: colors.accent, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                            Opção {idx + 2}
                          </span>
                          <span style={{ fontSize: "12px", fontWeight: 700, color: diffColor }}>{diffStr}</span>
                          {child.status === "published"
                            ? <span style={{ fontSize: "10px", background: "#d1fae5", color: "#065f46", padding: "1px 6px", borderRadius: "4px", fontWeight: 600 }}>Publicada</span>
                            : <span style={{ fontSize: "10px", background: "#fef3c7", color: "#92400e", padding: "1px 6px", borderRadius: "4px", fontWeight: 600 }}>Rascunho</span>
                          }
                          {quote.chosen_option_id === child.id && (
                            <span style={{ fontSize: "10px", background: "#dbeafe", color: "#1e40af", padding: "1px 8px", borderRadius: "4px", fontWeight: 700 }}>
                              ✓ Cliente escolheu esta
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: "13px", color: colors.text.secondary }}>
                          {airline && <span style={{ marginRight: "8px" }}>{airline}</span>}
                          {depTime} → {arrTime}
                          {" · "}
                          <span style={{ fontWeight: 600, color: colors.primary }}>
                            R$ {((child.final_price || child.total_price || 0)).toFixed(2).replace(".", ",")}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Remover opção ${idx + 2}?`)) return
                          await api.deleteQuote(child.id)
                          refresh()
                        }}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", fontSize: "18px", padding: "4px 8px", borderRadius: "4px" }}
                        title="Remover opção"
                      >
                        ✕
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Preços e Markup */}
          <div
            style={{
              marginTop: "1.5rem",
              paddingTop: "1.5rem",
              borderTop: `1px solid ${colors.border}`,
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "stretch" : "flex-end",
              gap: isMobile ? "1rem" : 0
            }}
          >
            <div>
              {/* Preço base */}
              <div style={{ marginBottom: "0.75rem" }}>
                <p style={{ fontSize: "0.75rem", color: colors.text.muted, margin: "0 0 0.25rem 0" }}>
                  Preço base (soma dos itens)
                </p>
                <p style={{ fontSize: "1.25rem", fontWeight: 600, color: colors.text.secondary, margin: 0 }}>
                  R$ {formatPrice(totalPrice)}
                </p>
              </div>

              {/* Markup */}
              <div style={{ marginBottom: "0.75rem" }}>
                <p style={{ fontSize: "0.75rem", color: colors.text.muted, margin: "0 0 0.25rem 0" }}>
                  Markup (sua margem)
                </p>
                {editingMarkup ? (
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <span style={{ color: colors.text.secondary }}>R$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={markupValue}
                      onChange={(e) => setMarkupValue(e.target.value)}
                      style={{
                        width: "120px",
                        padding: "0.5rem",
                        border: `1px solid ${colors.border}`,
                        borderRadius: borderRadius.sm,
                        fontSize: "1rem"
                      }}
                      autoFocus
                    />
                    <button
                      onClick={handleMarkupSave}
                      style={{
                        padding: "0.5rem 1rem",
                        backgroundColor: colors.accent,
                        color: colors.white,
                        border: "none",
                        borderRadius: borderRadius.sm,
                        cursor: "pointer",
                        fontSize: "0.875rem"
                      }}
                    >
                      Salvar
                    </button>
                    <button
                      onClick={() => setEditingMarkup(false)}
                      style={{
                        padding: "0.5rem 1rem",
                        backgroundColor: "transparent",
                        color: colors.text.muted,
                        border: `1px solid ${colors.border}`,
                        borderRadius: borderRadius.sm,
                        cursor: "pointer",
                        fontSize: "0.875rem"
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <p
                    onClick={startEditingMarkup}
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: 600,
                      color: markup > 0 ? colors.accent : colors.text.muted,
                      margin: 0,
                      cursor: "pointer",
                      textDecoration: "underline",
                      textDecorationStyle: "dotted"
                    }}
                    title="Clique para editar"
                  >
                    R$ {formatPrice(markup)}
                  </p>
                )}
              </div>

              {/* Preço final (o que o cliente vê) */}
              <div>
                <p style={{ fontSize: "0.875rem", color: colors.text.secondary, margin: "0 0 0.25rem 0" }}>
                  Valor para o cliente
                </p>
                <p style={{ fontSize: "2.5rem", fontWeight: 700, color: colors.primary, margin: 0 }}>
                  R$ {formatPrice(finalPrice)}
                </p>
              </div>
            </div>

            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
              {/* Copiar link */}
              {quote.status !== "draft" && (
                <button onClick={copyLink} style={buttonOutline} title="Copiar link">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: "0.375rem" }}>
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                  Copiar link
                </button>
              )}

              {/* Enviar via WhatsApp */}
              {quote.status === "published" && (
                <button
                  onClick={handleSendWhatsApp}
                  disabled={sending}
                  style={{
                    ...buttonPrimary,
                    backgroundColor: "#25d366",
                    opacity: sending ? 0.7 : 1
                  }}
                >
                  {sending ? "⏳ Enviando..." : "📲 Enviar"}
                </button>
              )}

              {/* Publicar — apenas draft */}
              {quote.status === "draft" && (
                <button
                  onClick={handlePublish}
                  disabled={publishing}
                  style={{
                    ...buttonPrimary,
                    opacity: publishing ? 0.7 : 1,
                    cursor: publishing ? 'wait' : 'pointer'
                  }}
                >
                  {publishing ? '⏳ Verificando imagens...' : 'Publicar cotação'}
                </button>
              )}
            </div>
          </div>

          {/* Card de Cliente — atalho de volta para a conversa */}
          {contactInfo && (
            <div style={{
              marginTop: "1.5rem",
              padding: "1rem",
              backgroundColor: "#f8fafc",
              borderRadius: "8px",
              border: "1px solid #e2e8f0"
            }}>
              <div style={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600, marginBottom: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                👤 Cliente
              </div>
              <a
                href={`/app/conversas?contact_id=${contactInfo.id}`}
                style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "0.75rem", cursor: "pointer", padding: "0.5rem", borderRadius: "8px", transition: "background 0.15s" }}
                title="Ir para a conversa"
                onMouseEnter={e => e.currentTarget.style.background = "#f0f4ff"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                {contactInfo.avatar_url ? (
                  <img
                    src={contactInfo.avatar_url}
                    alt=""
                    style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                    onError={(e) => {
                      e.target.style.display = "none"
                      e.target.nextSibling.style.display = "flex"
                    }}
                  />
                ) : null}
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#09077D", color: "white", display: contactInfo.avatar_url ? "none" : "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, flexShrink: 0 }}>
                  {(contactInfo.display_name || contactInfo.name || "?")[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: "1rem", fontWeight: 700, color: "#09077D", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                    {contactInfo.display_name || contactInfo.name}
                    <span style={{ fontSize: "0.75rem", color: "#50CFAD" }}>↗</span>
                  </div>
                  {contactInfo.phone && (
                    <div style={{ fontSize: "0.875rem", color: "#64748b", fontFamily: "monospace" }}>
                      {contactInfo.phone}
                    </div>
                  )}
                </div>
              </a>
            </div>
          )}

          {/* Chat de correção por IA — visível após publicação */}
          {quote.status !== "draft" && (
            <div
              style={{
                marginTop: "1.5rem",
                paddingTop: "1.5rem",
                borderTop: `1px solid ${colors.border}`
              }}
            >
              <p
                style={{
                  fontSize: "0.875rem",
                  color: colors.text.secondary,
                  margin: "0 0 0.75rem 0"
                }}
              >
                🤖 Correção por IA — descreva o que precisa ajustar:
              </p>
              <div style={{ display: "flex", gap: "0.75rem" }}>
                <input
                  type="text"
                  value={aiInstruction}
                  onChange={(e) => setAiInstruction(e.target.value)}
                  placeholder='Ex: "bagagem é só item pessoal, sem despacho" ou "cobertura do seguro é 15 mil dólares"'
                  disabled={aiCorrecting}
                  style={{
                    flex: 1,
                    padding: "0.75rem 1rem",
                    borderRadius: borderRadius.sm,
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.background,
                    color: colors.text.primary,
                    fontSize: "0.875rem"
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleAiCorrect()
                    }
                  }}
                />
                <button
                  onClick={handleAiCorrect}
                  disabled={aiCorrecting || !aiInstruction.trim()}
                  style={{
                    padding: "0.75rem 1.25rem",
                    backgroundColor:
                      aiCorrecting || !aiInstruction.trim()
                        ? "#ccc"
                        : colors.primary,
                    color: "white",
                    border: "none",
                    borderRadius: borderRadius.sm,
                    fontWeight: 500,
                    cursor:
                      aiCorrecting || !aiInstruction.trim()
                        ? "not-allowed"
                        : "pointer",
                    whiteSpace: "nowrap",
                    fontSize: "0.875rem"
                  }}
                >
                  {aiCorrecting ? "⏳ Corrigindo..." : "Corrigir"}
                </button>
              </div>

              {aiResult && (
                <div
                  style={{
                    marginTop: "0.75rem",
                    padding: "0.75rem 1rem",
                    borderRadius: borderRadius.sm,
                    backgroundColor:
                      aiResult.type === "success"
                        ? "rgba(16, 185, 129, 0.1)"
                        : "rgba(239, 68, 68, 0.1)",
                    border: `1px solid ${
                      aiResult.type === "success" ? "#10b981" : "#ef4444"
                    }`,
                    fontSize: "0.85rem",
                    color:
                      aiResult.type === "success" ? "#065f46" : "#991b1b"
                  }}
                >
                  {aiResult.type === "success" ? "✅" : "❌"}{" "}
                  {aiResult.message}
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Modal de adicionar bloco */}
      {showBlockForm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}
        >
          <div
            style={{
              backgroundColor: colors.card,
              borderRadius: borderRadius.lg,
              padding: "2rem",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflow: "auto"
            }}
          >
            <h2 style={{ fontSize: "1.5rem", fontWeight: 600, color: colors.primary, marginBottom: "1.5rem" }}>
              Adicionar Item
            </h2>

            <form onSubmit={handleAddBlock}>
              <div style={{ marginBottom: "1rem" }}>
                <label style={{ display: "block", fontSize: "0.875rem", color: colors.text.secondary, marginBottom: "0.5rem" }}>
                  Tipo
                </label>
                <select
                  value={blockForm.type}
                  onChange={(e) => setBlockForm({ ...blockForm, type: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `1px solid ${colors.border}`,
                    borderRadius: borderRadius.sm,
                    fontSize: "1rem",
                    boxSizing: "border-box"
                  }}
                >
                  {blockTypes.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.icon} {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <label style={{ display: "block", fontSize: "0.875rem", color: colors.text.secondary, marginBottom: "0.5rem" }}>
                  Data
                </label>
                <input
                  type="date"
                  value={blockForm.date}
                  onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })}
                  required
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `1px solid ${colors.border}`,
                    borderRadius: borderRadius.sm,
                    fontSize: "1rem",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <label style={{ display: "block", fontSize: "0.875rem", color: colors.text.secondary, marginBottom: "0.5rem" }}>
                  Título
                </label>
                <input
                  type="text"
                  value={blockForm.title}
                  onChange={(e) => setBlockForm({ ...blockForm, title: e.target.value })}
                  placeholder="Ex: LATAM - VDC → GRU"
                  required
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `1px solid ${colors.border}`,
                    borderRadius: borderRadius.sm,
                    fontSize: "1rem",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <label style={{ display: "block", fontSize: "0.875rem", color: colors.text.secondary, marginBottom: "0.5rem" }}>
                  Descrição
                </label>
                <textarea
                  value={blockForm.description}
                  onChange={(e) => setBlockForm({ ...blockForm, description: e.target.value })}
                  placeholder="Detalhes..."
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `1px solid ${colors.border}`,
                    borderRadius: borderRadius.sm,
                    fontSize: "1rem",
                    minHeight: "80px",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div style={{ marginBottom: "1rem" }}>
                <label style={{ display: "block", fontSize: "0.875rem", color: colors.text.secondary, marginBottom: "0.5rem" }}>
                  Preço (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={blockForm.price}
                  onChange={(e) => setBlockForm({ ...blockForm, price: e.target.value })}
                  required
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    border: `1px solid ${colors.border}`,
                    borderRadius: borderRadius.sm,
                    fontSize: "1rem",
                    boxSizing: "border-box"
                  }}
                />
              </div>

              <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
                <button
                  type="button"
                  onClick={() => setShowBlockForm(false)}
                  style={{ ...buttonOutline, flex: 1, justifyContent: "center" }}
                >
                  Cancelar
                </button>
                <button type="submit" style={{ ...buttonPrimary, flex: 1, justifyContent: "center" }}>
                  Adicionar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de nova opção (derive) */}
      {showDeriveModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}
        >
          <div
            style={{
              backgroundColor: colors.card,
              borderRadius: borderRadius.lg,
              padding: "2rem",
              width: "100%",
              maxWidth: "440px"
            }}
          >
            <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: colors.primary, marginBottom: "0.5rem" }}>
              + Nova Opção
            </h2>
            <p style={{ fontSize: "0.875rem", color: colors.text.secondary, margin: "0 0 1.5rem 0" }}>
              Cria uma cópia desta cotação para montar uma variação (ex: hotel diferente, voo mais barato).
              O cliente verá as opções como abas no mesmo link.
            </p>

            <div style={{ marginBottom: "1.5rem" }}>
              <label style={{ display: "block", fontSize: "0.875rem", color: colors.text.secondary, marginBottom: "0.5rem" }}>
                Nome da opção (opcional)
              </label>
              <input
                type="text"
                value={optionName}
                onChange={(e) => setOptionName(e.target.value)}
                placeholder='Ex: "Conforto", "Econômico", "Premium"'
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  border: `1px solid ${colors.border}`,
                  borderRadius: borderRadius.sm,
                  fontSize: "1rem",
                  boxSizing: "border-box"
                }}
                autoFocus
              />
            </div>

            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                onClick={() => { setShowDeriveModal(false); setOptionName('') }}
                disabled={deriving}
                style={{ ...buttonOutline, flex: 1, justifyContent: "center" }}
              >
                Cancelar
              </button>
              <button
                onClick={handleDerive}
                disabled={deriving}
                style={{
                  ...buttonPrimary,
                  flex: 1,
                  justifyContent: "center",
                  backgroundColor: "#8b5cf6",
                  opacity: deriving ? 0.7 : 1
                }}
              >
                {deriving ? "⏳ Criando..." : "Criar opção"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de imagens (verificação no publish) */}
      {showImagesModal && (
        <ImagesModal
          destination={quote?.destination}
          imageUrl1={imageUrl1}
          imageUrl2={imageUrl2}
          setImageUrl1={setImageUrl1}
          setImageUrl2={setImageUrl2}
          existingImages={existingImages}
          hotelImages={hotelImages}
          carImages={carImages}
          tourImages={tourImages}
          onImageChange={handleImageChange}
          onSubmit={handleImagesSubmit}
          onBack={() => setShowImagesModal(false)}
          loading={publishing}
          isValid={validateImages()}
        />
      )}
    </div>
  )
}
